import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  AttachMoney,
  CalendarMonth,
  Home,
  Person,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ar, enUS } from 'date-fns/locale';
import { contractsApi } from '../../../lib/api';
import { uploadContractDocument } from '../../../lib/imageUpload.web';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';

type PaymentFrequency = 'monthly' | 'quarterly' | 'biannually' | 'annually';
type ContractStatus = 'active' | 'expired' | 'terminated' | 'draft' | 'renewal';

// Only include fields that exist in the contracts table
interface FormData {
  start_date: Date | null;
  end_date: Date | null;
  rent_amount: string;
  security_deposit: string;
  payment_frequency: PaymentFrequency;
  status: ContractStatus;
  is_foreign_tenant: boolean;
}

export default function EditContract() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['contracts', 'common']);
  const navigate = useNavigate();
  const language = useAppStore((state) => state.settings.language);
  const isRTL = language === 'ar';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    start_date: null,
    end_date: null,
    rent_amount: '',
    security_deposit: '',
    payment_frequency: 'monthly',
    status: 'active',
    is_foreign_tenant: false,
  });

  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [existingDocumentUrl, setExistingDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch contract details
  const {
    data: contract,
    loading: contractLoading,
    error: contractError,
  } = useApi(() => (id ? contractsApi.getById(id) : Promise.resolve({ data: null, error: null })), [id]);

  // Populate form when contract data is loaded
  useEffect(() => {
    if (contract) {
      setFormData({
        start_date: contract.start_date ? new Date(contract.start_date) : null,
        end_date: contract.end_date ? new Date(contract.end_date) : null,
        rent_amount: contract.rent_amount?.toString() || '',
        security_deposit: contract.security_deposit?.toString() || '',
        payment_frequency: contract.payment_frequency || 'monthly',
        status: contract.status || 'active',
        is_foreign_tenant: contract.is_foreign_tenant || false,
      });
      setExistingDocumentUrl(contract.contract_document_url || null);
    }
  }, [contract]);

  const handleChange = (field: keyof FormData) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleDateChange = (field: 'start_date' | 'end_date') => (date: Date | null) => {
    setFormData({ ...formData, [field]: date });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, document: t('invalidDocumentType') });
        return;
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        setErrors({ ...errors, document: t('documentTooLarge') });
        return;
      }

      setDocumentFile(file);
      setErrors({ ...errors, document: '' });
    }
  };

  const handleRemoveDocument = () => {
    setDocumentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.start_date) {
      newErrors.start_date = t('common:required');
    }

    if (!formData.end_date) {
      newErrors.end_date = t('common:required');
    }

    if (formData.start_date && formData.end_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = t('endDateMustBeAfterStart');
    }

    if (!formData.rent_amount || isNaN(Number(formData.rent_amount)) || Number(formData.rent_amount) <= 0) {
      newErrors.rent_amount = t('invalidAmount');
    }

    if (!formData.security_deposit || isNaN(Number(formData.security_deposit))) {
      newErrors.security_deposit = t('invalidAmount');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !id) return;

    setLoading(true);
    setSubmitError(null);
    setSuccessMessage(null);

    try {
      let documentUrl = existingDocumentUrl;

      // Upload new document if provided
      if (documentFile) {
        setUploadProgress(true);
        const uploadResult = await uploadContractDocument(documentFile);
        setUploadProgress(false);

        if (!uploadResult.success) {
          setSubmitError(uploadResult.error || t('documentUploadFailed'));
          setLoading(false);
          return;
        }
        documentUrl = uploadResult.url || null;
      }

      // Update contract - only include fields that exist in the database
      const updateData = {
        start_date: formData.start_date?.toISOString(),
        end_date: formData.end_date?.toISOString(),
        rent_amount: Number(formData.rent_amount),
        security_deposit: Number(formData.security_deposit),
        payment_frequency: formData.payment_frequency,
        status: formData.status,
        is_foreign_tenant: formData.is_foreign_tenant,
        contract_document_url: documentUrl,
      };

      const response = await contractsApi.update(id, updateData);

      if (response.error) {
        setSubmitError(typeof response.error === 'string' ? response.error : response.error.message || t('common:errorOccurred'));
        return;
      }

      setSuccessMessage(t('contractUpdatedSuccess'));

      // Navigate back to contract detail after short delay
      setTimeout(() => {
        navigate(`/dashboard/contracts/${id}`);
      }, 1500);
    } catch (err: any) {
      setSubmitError(err.message || t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusColor = (status: ContractStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'terminated':
        return 'error';
      case 'draft':
        return 'default';
      case 'renewal':
        return 'info';
      default:
        return 'default';
    }
  };

  const BackArrow = isRTL ? ArrowForward : ArrowBack;

  if (contractLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (contractError || !contract) {
    return (
      <Box p={3}>
        <Alert severity="error">{contractError?.message || t('common:error')}</Alert>
        <Button
          startIcon={isRTL ? <ArrowForward /> : <ArrowBack />}
          onClick={() => navigate('/dashboard/contracts')}
          sx={{ mt: 2 }}
        >
          {t('common:back')}
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isRTL ? ar : enUS}>
      <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(`/dashboard/contracts/${id}`)}>
            <BackArrow />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {t('editContract')}
          </Typography>
          <Chip
            label={t(`status${formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}`)}
            color={getStatusColor(formData.status)}
            size="small"
          />
        </Box>

        {/* Success/Error Alerts */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Property & Tenant Info (Read-only) */}
        <Card sx={{ mb: 3, bgcolor: 'action.hover' }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Property Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Home color="secondary" />
                  <Typography variant="subtitle1" fontWeight="bold">{t('property')}</Typography>
                </Box>
                {contract.property ? (
                  <Box>
                    <Typography variant="body1">
                      {(contract.property as any).title || (contract.property as any).name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(contract.property as any).address}
                    </Typography>
                  </Box>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Grid>

              {/* Tenant Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Person color="info" />
                  <Typography variant="subtitle1" fontWeight="bold">{t('tenant')}</Typography>
                </Box>
                {contract.tenant ? (
                  <Box>
                    <Typography variant="body1">
                      {(contract.tenant as any).first_name} {(contract.tenant as any).last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(contract.tenant as any).email}
                    </Typography>
                  </Box>
                ) : (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contract Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DocumentIcon color="primary" />
              <Typography variant="h6">{t('status')}</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('status')}</InputLabel>
                  <Select
                    value={formData.status}
                    label={t('status')}
                    onChange={handleChange('status')}
                  >
                    <MenuItem value="active">{t('statusActive')}</MenuItem>
                    <MenuItem value="draft">{t('statusDraft')}</MenuItem>
                    <MenuItem value="expired">{t('statusExpired')}</MenuItem>
                    <MenuItem value="terminated">{t('statusTerminated')}</MenuItem>
                    <MenuItem value="renewal">{t('statusRenewal')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_foreign_tenant}
                      onChange={handleChange('is_foreign_tenant')}
                    />
                  }
                  label={t('common:foreignTenant')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contract Duration */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="warning" />
              <Typography variant="h6">{t('contractDuration')}</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t('startDate')}
                  value={formData.start_date}
                  onChange={handleDateChange('start_date')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.start_date,
                      helperText: errors.start_date,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t('endDate')}
                  value={formData.end_date}
                  onChange={handleDateChange('end_date')}
                  minDate={formData.start_date || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.end_date,
                      helperText: errors.end_date,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Financial Terms */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoney color="success" />
              <Typography variant="h6">{t('financialTerms')}</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('rentAmount')}
                  value={formData.rent_amount}
                  onChange={handleChange('rent_amount')}
                  type="number"
                  error={!!errors.rent_amount}
                  helperText={errors.rent_amount}
                  InputProps={{
                    endAdornment: <Typography color="text.secondary">SAR</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('securityDeposit')}
                  value={formData.security_deposit}
                  onChange={handleChange('security_deposit')}
                  type="number"
                  error={!!errors.security_deposit}
                  helperText={errors.security_deposit}
                  InputProps={{
                    endAdornment: <Typography color="text.secondary">SAR</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('paymentFrequency')}</InputLabel>
                  <Select
                    value={formData.payment_frequency}
                    label={t('paymentFrequency')}
                    onChange={handleChange('payment_frequency')}
                  >
                    <MenuItem value="monthly">{t('frequencyMonthly')}</MenuItem>
                    <MenuItem value="quarterly">{t('frequencyQuarterly')}</MenuItem>
                    <MenuItem value="biannually">{t('frequencyBiannually')}</MenuItem>
                    <MenuItem value="annually">{t('frequencyAnnually')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contract Document Upload */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <UploadIcon color="primary" />
              <Typography variant="h6">{t('contractDocument')}</Typography>
            </Box>

            {/* Existing Document */}
            {existingDocumentUrl && !documentFile && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'success.50',
                  borderColor: 'success.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DocumentIcon color="success" fontSize="large" />
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {t('existingDocument')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('documentUploaded')}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  href={existingDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenIcon />}
                  size="small"
                >
                  {t('viewDocument')}
                </Button>
              </Paper>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {existingDocumentUrl ? t('replaceDocumentHelp') : t('contractDocumentHelp')}
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="contract-document-input"
            />

            {documentFile ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'action.hover',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DocumentIcon color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {documentFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(documentFile.size)} - {t('newDocument')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleRemoveDocument} color="error">
                  <DeleteIcon />
                </IconButton>
              </Paper>
            ) : (
              <Button
                variant="outlined"
                component="label"
                htmlFor="contract-document-input"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ py: 3, borderStyle: 'dashed' }}
              >
                {existingDocumentUrl ? t('replaceDocument') : t('uploadDocument')}
              </Button>
            )}
            {errors.document && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {errors.document}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate(`/dashboard/contracts/${id}`)} disabled={loading}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {uploadProgress ? t('uploadingDocument') : loading ? t('common:saving') : t('updateContract')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
